import { useEffect, useState, useRef } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
  BarController,
} from "chart.js";
import api from "../api";
import { FastField } from "formik";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  ArcElement,
  Tooltip,
  Legend
);

function niceMax(raw) {
  if (!raw || raw === 0) return 10;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const f = raw / mag;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nice * mag;
}

const LEGEND = [
  // { label: "All Transactions", color: "#60a5fa" },
  { label: "Successful", color: "#4ade80" },
  { label: "Failed", color: "#f87171" },
];

const Y_AXIS_ONLY_PLUGIN = {
  id: "yAxisOnly",
  afterDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea || !scales.x) return;
    const cutX = scales.x.left;
    ctx.save();
    ctx.clearRect(cutX, 0, chart.width - cutX, chart.height);
    ctx.restore();
  },
};
function build12Months(all, year) {
  const map = {};

  all.forEach((d) => {
    map[d.monthKey] = d;
  });

  const result = [];

  for (let month = 1; month <= 12; month++) {
    const key = `${year}-${String(month).padStart(2, "0")}`;

    const date = new Date(year, month - 1);

    const monthName = date.toLocaleString("en", {
      month: "short",
    });

    result.push(
      map[key] || {
        monthKey: key,
        monthName,
        total: 0,
        success: 0,
        failed: 0,
      }
    );
  }

  return result;
}

export function RevenueChart({ selectedYear }) {
  const [rawData, setRawData] = useState([]);
  const [yMax, setYMax] = useState(10);
  const [ready, setReady] = useState(false);

  const mainCanvas = useRef(null);
  const yCanvas = useRef(null);
  const chartInst = useRef(null);
  const yChartInst = useRef(null);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("jwt");
       const res = await api.post(
  `/forms/metrics/monthly/all?year=${selectedYear}`,
  {},
  {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  }
);
        // const all  = Array.isArray(res.data) ? res.data : [];
        // // const data = all.slice(-6); // ← latest 6 months only
        const all = res.data.metrics || [];
const data = build12Months(all, selectedYear);

        const maxTotal = Math.max(...data.map(d => d.total), 0);
        setYMax(niceMax(maxTotal));
        setRawData(data);
        setReady(true);
      } catch (e) {
        console.error("metrics error", e);
      }
    })();
  }, [selectedYear]);



  // ── Main bar chart ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready || !mainCanvas.current) return;

    if (chartInst.current) { chartInst.current.destroy(); chartInst.current = null; }

    chartInst.current = new ChartJS(mainCanvas.current, {
      type: "bar",
      data: {
        labels: rawData.map(d => d.monthName),
        datasets: [
          // {
          //   label: "Total Transactions",
          //   data: rawData.map(d => d.total),
          //   backgroundColor: "#60a5fa",
          //   borderRadius: 4,
          //   stack: "s",
          // },
          {
            label: "Successful",
            data: rawData.map(d => d.success),
            backgroundColor: "#4ade80",
            borderRadius: 4,
            stack: "s",
            maxBarThickness: 70,   
          },
          {
            label: "Failed",
            data: rawData.map(d => d.failed),
            backgroundColor: "#f87171",
            borderRadius: 4,
            stack: "s",
            maxBarThickness: 70,   
          },
        ],
      },
      options: {
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: { label: ctx => `${ctx.dataset.label}: ${ctx.raw}` },
          },
        },
        scales: {
          x: {
            stacked: true,
            grid: { display: false },
            ticks: { color: "#6b7280" },
            barPercentage: 0.7,  // ← controls bar width relative to category slot
            categoryPercentage: 0.5,  // ← controls category slot width
          },
          y: {
            stacked: true,
            display: false,
            beginAtZero: true,
            max: yMax,
          },
        },
      },
    });

    return () => {
      if (chartInst.current) { chartInst.current.destroy(); chartInst.current = null; }
    };
  }, [ready, rawData, yMax]);

  // ── Ghost Y-axis chart ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!ready || !yCanvas.current) return;

    if (yChartInst.current) { yChartInst.current.destroy(); yChartInst.current = null; }

    yChartInst.current = new ChartJS(yCanvas.current, {
      type: "bar",
      data: {
        labels: [""],
        datasets: [{ data: [0], backgroundColor: "transparent", stack: "s" }],
      },
      options: {
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: { display: false },
          tooltip: { enabled: false },
        },
        scales: {
          x: {
            stacked: true,
            display: true,
            grid: { display: false },
            ticks: { display: false },
            border: { display: false },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            max: yMax,
            grid: { color: "rgba(15,23,42,0.06)" },
            ticks: { color: "#6b7280", maxTicksLimit: 6 },
            border: { display: true, color: "rgba(15,23,42,0.12)" },
          },
        },
      },
      plugins: [Y_AXIS_ONLY_PLUGIN],
    });

    return () => {
      if (yChartInst.current) { yChartInst.current.destroy(); yChartInst.current = null; }
    };
  }, [ready, yMax]);

  if (!ready) {
    return <p style={{ color: "#6b7280", fontSize: 14, padding: "8px 0" }}>Loading chart…</p>;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "320px" }}>

      {/* Legend */}
      <div style={{
        display: "flex", gap: 16, justifyContent: "center",
        paddingBottom: 10, flexShrink: 0,
      }}>
        {LEGEND.map(({ label, color }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{
              width: 8, height: 8, borderRadius: 2,
              backgroundColor: color, display: "inline-block", flexShrink: 0,
            }} />
            <span style={{ fontSize: 12, color: "#6b7280" }}>{label}</span>
          </div>
        ))}
      </div>

      {/* Chart body */}
      <div style={{ display: "flex", flex: 1, minHeight: 0 }}>

        {/* Fixed Y-axis panel */}
        <div style={{ flexShrink: 0, width: 60, position: "relative", paddingTop: 10 }}>
          <canvas
            ref={yCanvas}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
          />
        </div>

        {/* Bars area — no scroll, fills remaining width */}
        <div
  style={{
    flex: 1,
    overflowX: "auto",
    overflowY: "hidden",
  }}
>
  <div
    style={{
      position: "relative",
      height: "100%",
      width: "1200px",
    }}
  >
    <canvas
      ref={mainCanvas}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
      }}
    />
  </div>
</div>

      </div>
    </div>
  );
}

/* ================================================================
   Forms Status Doughnut Chart
================================================================ */
export function FormsStatusChart() {
  const [activeCount, setActiveCount] = useState(0);
  const [inactiveCount, setInactiveCount] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("jwt");
        const res = await api.post(
          "/forms/fetchAllForms", {},
          { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
        );
        const data = Array.isArray(res.data) ? res.data : [res.data];
        setActiveCount(data.filter(f => f.isActive).length);
        setInactiveCount(data.filter(f => !f.isActive).length);
      } catch (e) {
        console.error("forms error", e);
      }
    })();
  }, []);

  return (
    <div className="px-chart">
      <Doughnut
        data={{
          labels: ["Active Forms", "Inactive Forms"],
          datasets: [{
            data: [activeCount, inactiveCount],
            backgroundColor: ["#34d399", "#f87171"],
            borderWidth: 0,
          }],
        }}
        options={{
          cutout: "70%",
          plugins: {
            legend: {
              position: "bottom",
              labels: { color: "#6b7280", boxWidth: 10 },
            },
          },
        }}
      />
    </div>
  );
}

/* ================================================================
   Dashboard
================================================================ */
export default function Dashboard() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <RevenueChart />
      <FormsStatusChart />
    </div>
  );
}